import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Row, Col, Card, Button, Form, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';
import * as moment from 'moment';
import DataTable from 'react-data-table-component';
import Select from 'react-select';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx-js-style';

import { DEV_SERVER } from '../../../constants/urls';
import { userSignal } from '../../../signal/userSignal';
import { allUsersSignal } from '../../../signal/allUsersSignal';
import { organisationSignal } from '../../../signal/organisationSignal';
import avatar1 from '../../../assets/images/avatars/01.png';
import EditTripModal from '../trips/EditTripModal';

export default function PerformanceAnalysis() {
    // Period options: Current Month, 1 Month (Last 30 Days), 3 Months (Last 90 Days)
    const [periodType, setPeriodType] = useState('CURRENT_MONTH');
    const [fromDate, setFromDate] = useState(moment().startOf('month').format('YYYY-MM-DD'));
    const [toDate, setToDate] = useState(moment().format('YYYY-MM-DD'));

    // Filters & search
    const [selectedState, setSelectedState] = useState({ value: 'ALL', label: 'All States' });
    const [selectedManager, setSelectedManager] = useState({ value: 'ALL', label: 'All Managers' });
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('bestPerformers');

    // Edit Trip Modal state
    const [selectedTripToEdit, setSelectedTripToEdit] = useState(null);
    const [showEditTripModal, setShowEditTripModal] = useState(false);

    // Raw datasets
    const [trips, setTrips] = useState([]);
    const [attendances, setAttendances] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Set date ranges according to preset selection
    const handlePeriodChange = (type) => {
        setPeriodType(type);
        if (type === 'CURRENT_MONTH') {
            setFromDate(moment().startOf('month').format('YYYY-MM-DD'));
            setToDate(moment().format('YYYY-MM-DD'));
        } else if (type === '1_MONTH') {
            setFromDate(moment().subtract(30, 'days').format('YYYY-MM-DD'));
            setToDate(moment().format('YYYY-MM-DD'));
        } else if (type === '3_MONTHS') {
            setFromDate(moment().subtract(90, 'days').format('YYYY-MM-DD'));
            setToDate(moment().format('YYYY-MM-DD'));
        }
    };

    // Ensure users and organisation settings are loaded
    useEffect(() => {
        if (!allUsersSignal.value || Object.keys(allUsersSignal.value).length === 0) {
            axios.get(`${DEV_SERVER}/auth/v2/employee/getEmployees`, {
                headers: {
                    Authorization: `Bearer ${userSignal.value?.token}`
                }
            }).then(resp => {
                if (resp.data?.status && Array.isArray(resp.data?.data)) {
                    let temp = {};
                    resp.data.data.forEach(item => {
                        temp[item.id] = item;
                    });
                    allUsersSignal.value = temp;
                }
            }).catch(err => console.error("Failed to fetch employees", err));
        }

        if (!organisationSignal.value) {
            axios.get(`${DEV_SERVER}/auth/v2/organisation/`, {
                headers: {
                    Authorization: `Bearer ${userSignal.value?.token}`
                }
            }).then(resp => {
                if (resp.data?.status) {
                    organisationSignal.value = resp.data.data;
                }
            }).catch(err => console.error("Failed to fetch organisation", err));
        }
    }, []);

    // Fetch Trips & Attendance data for selected date range
    const fetchData = useCallback(async () => {
        try {
            setIsLoading(true);
            const startTs = new Date(fromDate).setHours(0, 0, 0, 0);
            const endTs = new Date(toDate).setHours(23, 59, 59, 999);

            const [tripsRes, attendancesRes] = await Promise.all([
                axios.get(`${DEV_SERVER}/trip/v2/getAllTrips?startTime=${startTs}&endTime=${endTs}`, {
                    headers: { Authorization: `Bearer ${userSignal.value?.token}` }
                }),
                axios.get(`${DEV_SERVER}/auth/v2/attendence?startTime=${startTs}&endTime=${endTs}`, {
                    headers: { Authorization: `Bearer ${userSignal.value?.token}` }
                })
            ]);

            if (tripsRes.data?.status) {
                setTrips(tripsRes.data.data || []);
            } else {
                setTrips([]);
            }

            if (attendancesRes.data?.status) {
                setAttendances(attendancesRes.data.data || []);
            } else {
                setAttendances([]);
            }
        } catch (error) {
            console.error("Error fetching performance data:", error);
            toast.error("Failed to load performance data");
        } finally {
            setIsLoading(false);
        }
    }, [fromDate, toDate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Check if trip ended on the next day or later (or unended)
    const isNextDayTripEnd = (trip) => {
        if (!trip.isStarted) return false;
        const tripStartTs = trip.startTime || trip.date;
        if (!tripStartTs) return false;
        const endOfTripDay = moment(tripStartTs).endOf('day').valueOf();

        // Ended past 11:59:59 PM of the start date
        if (trip.isCompleted && trip.endTime) {
            return trip.endTime > endOfTripDay;
        }
        // Never ended
        if (trip.isNotEnded) return true;
        if (!trip.isCompleted && endOfTripDay < Date.now()) return true;

        return false;
    };

    // Calculate manual km (meters to km, or odometer start/end difference)
    const getTripManualKm = (trip) => {
        if (!trip) return 0;
        if (typeof trip.manualDistance === 'number' && trip.manualDistance > 0) {
            return parseFloat((trip.manualDistance / 1000).toFixed(2));
        }
        const start = parseFloat(trip.startKM);
        const end = parseFloat(trip.endKM);
        if (!isNaN(start) && !isNaN(end) && end > start) {
            return parseFloat((end - start).toFixed(2));
        }
        if (typeof trip.manualKM === 'number' && trip.manualKM > 0) {
            return parseFloat(trip.manualKM.toFixed(2));
        }
        return 0;
    };

    // Calculate auto calculated distance in km
    const getTripAutoKm = (trip) => {
        if (!trip) return 0;
        if (typeof trip.autoCalculatedDistance === 'number' && trip.autoCalculatedDistance > 0) {
            return parseFloat((trip.autoCalculatedDistance / 1000).toFixed(2));
        }
        if (typeof trip.totalDistance === 'number' && trip.totalDistance > 0) {
            return parseFloat((trip.totalDistance / 1000).toFixed(2));
        }
        return 0;
    };

    // Calculate aggregated employee metrics
    const employeeMetrics = useMemo(() => {
        const usersObj = allUsersSignal.value || {};
        const usersList = Object.values(usersObj).filter(u => !u.isTallySyncMaster);
        const orgHolidays = organisationSignal.value?.holidays || {};
        const isSatWorking = !!organisationSignal.value?.isSaturdayWorking;
        const isSunWorking = !!organisationSignal.value?.isSundayWorking;

        // Group trips by userId
        const tripsByUser = {};
        trips.forEach(trip => {
            if (!trip.userId) return;
            if (!tripsByUser[trip.userId]) {
                tripsByUser[trip.userId] = [];
            }
            tripsByUser[trip.userId].push(trip);
        });

        // Group attendances by userId -> date
        const attendancesByUser = {};
        attendances.forEach(att => {
            if (!att.userId) return;
            if (!attendancesByUser[att.userId]) {
                attendancesByUser[att.userId] = {};
            }
            const dayKey = moment(att.startTime).format('YYYY-MM-DD');
            const existing = attendancesByUser[att.userId][dayKey];
            if (!existing || att.isPresent) {
                attendancesByUser[att.userId][dayKey] = att;
            }
        });

        const startPeriod = moment(fromDate);
        const endPeriod = moment(toDate);

        // Precompute working days for the calendar span
        let calendarDays = [];
        let cur = moment(startPeriod);
        while (cur <= endPeriod && cur <= moment()) {
            calendarDays.push(cur.format('YYYY-MM-DD'));
            cur.add(1, 'days');
        }

        // Build metric per employee
        const metrics = usersList.map(emp => {
            const empTrips = tripsByUser[emp.id] || [];
            const empAttendances = attendancesByUser[emp.id] || {};

            // Trip & task calculations
            let totalDistanceMeters = 0;
            let tasksCompleted = 0;
            let nextDayTripCount = 0;
            let latestLateEndTs = null;

            empTrips.forEach(trip => {
                const dist = trip.totalDistance || trip.autoCalculatedDistance || trip.manualDistance || 0;
                totalDistanceMeters += dist;

                const tasks = (typeof trip.tasksCompleted === 'number')
                    ? trip.tasksCompleted
                    : (Array.isArray(trip.tasks) ? trip.tasks.filter(t => t.isCompleted || t.status === 'completed').length : 0);
                tasksCompleted += tasks;

                if (isNextDayTripEnd(trip)) {
                    nextDayTripCount += 1;
                    const endTs = trip.endTime || trip.startTime || trip.date;
                    if (!latestLateEndTs || endTs > latestLateEndTs) {
                        latestLateEndTs = endTs;
                    }
                }
            });

            const totalTrips = empTrips.length;
            const onTimeTrips = Math.max(0, totalTrips - nextDayTripCount);
            const onTimeRate = totalTrips > 0 ? Math.round((onTimeTrips / totalTrips) * 100) : 100;
            const lateEndRate = totalTrips > 0 ? Math.round((nextDayTripCount / totalTrips) * 100) : 0;
            const totalDistanceKm = parseFloat((totalDistanceMeters / 1000).toFixed(2));
            const avgVisitsPerTrip = totalTrips > 0 ? parseFloat((tasksCompleted / totalTrips).toFixed(1)) : 0;

            // Attendance calculations
            let presentDays = 0;
            let absentDays = 0;
            let leaveDays = 0;
            let weekOffs = 0;
            let holidays = 0;
            let workingDays = 0;

            const empCreatedAt = emp.createdAt ? moment(emp.createdAt).startOf('day') : moment(fromDate);

            calendarDays.forEach(dayStr => {
                const dayMoment = moment(dayStr);
                if (dayMoment < empCreatedAt) {
                    return; // Skip days before onboarding
                }

                const dayOfWeek = dayMoment.day(); // 0 is Sunday, 6 is Saturday
                const mmdd = dayMoment.format('DD/MM');
                const isHoliday = !!orgHolidays[mmdd];
                const isWeekOff = (dayOfWeek === 6 && !isSatWorking) || (dayOfWeek === 0 && !isSunWorking);

                if (isHoliday) {
                    holidays += 1;
                    return;
                }
                if (isWeekOff) {
                    weekOffs += 1;
                    return;
                }

                workingDays += 1;

                const attRecord = empAttendances[dayStr];
                if (attRecord) {
                    if (attRecord.isPresent) {
                        presentDays += 1;
                    } else if (attRecord.leaveId && attRecord.leaveId !== '000000000000000000000000') {
                        leaveDays += 1;
                    } else {
                        absentDays += 1;
                    }
                } else {
                    absentDays += 1;
                }
            });

            const attendanceRate = workingDays > 0 ? Math.round((presentDays / workingDays) * 100) : 0;
            const absenceRate = workingDays > 0 ? Math.round((absentDays / workingDays) * 100) : 0;

            // Manager mapping
            let empManagerId = emp.managerId;
            if (empManagerId && typeof empManagerId === 'object') {
                empManagerId = empManagerId.id || empManagerId._id;
            }
            const hasValidManager = empManagerId &&
                empManagerId !== "" &&
                empManagerId !== "000000000000000000000000" &&
                empManagerId !== "null" &&
                empManagerId !== "undefined";

            const managerName = hasValidManager && usersObj[empManagerId]
                ? usersObj[empManagerId].name
                : (hasValidManager ? (emp.managerName || 'Assigned Manager') : 'No Manager');

            return {
                id: emp.id,
                name: emp.name || 'Unnamed',
                phoneNumber: emp.phoneNumber || '',
                designation: emp.designation || 'Employee',
                state: emp.state ? emp.state.trim() : 'Unassigned',
                managerId: hasValidManager ? empManagerId : null,
                managerName: managerName,
                profileURL: emp.profileURL || '',
                totalTrips,
                totalDistanceKm,
                tasksCompleted,
                nextDayTripCount,
                onTimeTrips,
                onTimeRate,
                lateEndRate,
                latestLateEndTs,
                avgVisitsPerTrip,
                presentDays,
                absentDays,
                leaveDays,
                workingDays,
                weekOffs,
                holidays,
                attendanceRate,
                absenceRate
            };
        });

        // Compute cohorts max for score normalization
        const maxTasks = Math.max(...metrics.map(m => m.tasksCompleted), 1);
        const maxDistance = Math.max(...metrics.map(m => m.totalDistanceKm), 1);

        // Assign Best Performer Composite Score (out of 100)
        // Factors: Tasks completed (35%), Distance (25%), Attendance/Present regularity (25%), On-time trip completion (15%)
        metrics.forEach(m => {
            const taskScore = (m.tasksCompleted / maxTasks) * 35;
            const distanceScore = (m.totalDistanceKm / maxDistance) * 25;
            const attendanceScore = ((m.workingDays > 0 ? m.presentDays / m.workingDays : 1)) * 25;
            const tripDisciplineScore = (m.onTimeRate / 100) * 15;

            const composite = Math.round(taskScore + distanceScore + attendanceScore + tripDisciplineScore);
            m.bestPerformerScore = Math.min(100, Math.max(0, composite));
        });

        return metrics;
    }, [trips, attendances, fromDate, toDate]);

    // Available states for filtering
    const stateFilterOptions = useMemo(() => {
        const counts = {};
        let unassigned = 0;
        employeeMetrics.forEach(m => {
            if (m.state && m.state !== 'Unassigned') {
                counts[m.state] = (counts[m.state] || 0) + 1;
            } else {
                unassigned++;
            }
        });

        const activeStates = Object.keys(counts).sort((a, b) => a.localeCompare(b));
        const options = [
            { value: 'ALL', label: `All States (${employeeMetrics.length} employees)` }
        ];

        if (unassigned > 0) {
            options.push({ value: 'Unassigned', label: `Unassigned (${unassigned} employees)` });
        }

        activeStates.forEach(st => {
            options.push({
                value: st,
                label: `${st} (${counts[st]} ${counts[st] === 1 ? 'employee' : 'employees'})`
            });
        });

        return options;
    }, [employeeMetrics]);

    // Available managers for filtering
    const managerFilterOptions = useMemo(() => {
        const usersObj = allUsersSignal.value || {};
        const counts = {};
        const managerNames = {};
        let unassigned = 0;

        employeeMetrics.forEach(m => {
            if (m.managerId) {
                counts[m.managerId] = (counts[m.managerId] || 0) + 1;
                managerNames[m.managerId] = m.managerName;
            } else {
                unassigned++;
            }
        });

        // Include users registered as manager or admin in organisation even if currently 0 direct reports
        Object.values(usersObj).forEach(u => {
            if ((u.userType === 'manager' || u.isAdmin) && !u.isTallySyncMaster && u.id) {
                if (counts[u.id] === undefined) {
                    counts[u.id] = 0;
                    managerNames[u.id] = u.name || 'Unnamed Manager';
                }
            }
        });

        const activeManagerIds = Object.keys(counts).sort((a, b) => {
            const nameA = (managerNames[a] || '').toLowerCase();
            const nameB = (managerNames[b] || '').toLowerCase();
            return nameA.localeCompare(nameB);
        });

        const options = [
            { value: 'ALL', label: `All Managers (${employeeMetrics.length} employees)` }
        ];

        if (unassigned > 0) {
            options.push({ value: 'NO_MANAGER', label: `No Manager (${unassigned} employees)` });
        }

        activeManagerIds.forEach(mId => {
            const name = managerNames[mId] || 'Unknown Manager';
            const count = counts[mId] || 0;
            options.push({
                value: mId,
                label: `${name} (${count} ${count === 1 ? 'employee' : 'employees'})`
            });
        });

        return options;
    }, [employeeMetrics]);

    // Apply State, Manager, and Search filter to cohort
    const filteredCohort = useMemo(() => {
        return employeeMetrics.filter(m => {
            // State filter
            if (selectedState && selectedState.value !== 'ALL') {
                if (m.state.toLowerCase() !== selectedState.value.toLowerCase()) {
                    return false;
                }
            }
            // Manager filter
            if (selectedManager && selectedManager.value !== 'ALL') {
                if (selectedManager.value === 'NO_MANAGER') {
                    if (m.managerId) return false;
                } else {
                    if (m.managerId !== selectedManager.value) {
                        return false;
                    }
                }
            }
            // Search filter
            if (searchTerm.trim() !== '') {
                const term = searchTerm.toLowerCase();
                const matchName = m.name.toLowerCase().includes(term);
                const matchPhone = m.phoneNumber.toString().includes(term);
                const matchDesignation = m.designation.toLowerCase().includes(term);
                const matchState = m.state.toLowerCase().includes(term);
                const matchManager = (m.managerName || '').toLowerCase().includes(term);
                if (!matchName && !matchPhone && !matchDesignation && !matchState && !matchManager) {
                    return false;
                }
            }
            return true;
        });
    }, [employeeMetrics, selectedState, selectedManager, searchTerm]);

    // 1. Best Performers dataset (sorted by Score descending)
    const bestPerformersData = useMemo(() => {
        return [...filteredCohort].sort((a, b) => {
            if (b.bestPerformerScore !== a.bestPerformerScore) {
                return b.bestPerformerScore - a.bestPerformerScore;
            }
            if (b.tasksCompleted !== a.tasksCompleted) {
                return b.tasksCompleted - a.tasksCompleted;
            }
            return b.totalDistanceKm - a.totalDistanceKm;
        });
    }, [filteredCohort]);

    // 2. Irregular Employees dataset (sorted by Absent Days descending)
    const irregularEmployeesData = useMemo(() => {
        return [...filteredCohort]
            .sort((a, b) => {
                if (b.absentDays !== a.absentDays) {
                    return b.absentDays - a.absentDays;
                }
                return a.presentDays - b.presentDays;
            });
    }, [filteredCohort]);

    // 3. Less Visit Employees dataset (sorted by Tasks Completed ascending)
    const lessVisitEmployeesData = useMemo(() => {
        return [...filteredCohort]
            .sort((a, b) => {
                if (a.tasksCompleted !== b.tasksCompleted) {
                    return a.tasksCompleted - b.tasksCompleted;
                }
                return b.totalTrips - a.totalTrips;
            });
    }, [filteredCohort]);

    // 4. Next-Day Trip End Employees dataset (sorted by Next-Day count descending)
    const nextDayTripEmployeesData = useMemo(() => {
        return [...filteredCohort]
            .sort((a, b) => {
                if (b.nextDayTripCount !== a.nextDayTripCount) {
                    return b.nextDayTripCount - a.nextDayTripCount;
                }
                return b.lateEndRate - a.lateEndRate;
            });
    }, [filteredCohort]);

    // 5. Excess KM Trips (> 1000 KM) dataset
    const excessKmTripsData = useMemo(() => {
        const userMap = allUsersSignal.value || {};
        const list = [];

        trips.forEach(trip => {
            const manualKm = getTripManualKm(trip);
            if (manualKm > 1000) {
                const emp = userMap[trip.userId] || { id: trip.userId, name: trip.userName || 'Unknown Employee' };
                const empState = emp.state ? emp.state.trim() : 'Unassigned';

                // State filter
                if (selectedState && selectedState.value !== 'ALL') {
                    if (empState.toLowerCase() !== selectedState.value.toLowerCase()) {
                        return;
                    }
                }

                // Manager filter
                let empManagerId = emp.managerId;
                if (empManagerId && typeof empManagerId === 'object') {
                    empManagerId = empManagerId.id || empManagerId._id;
                }
                const hasValidManager = empManagerId &&
                    empManagerId !== "" &&
                    empManagerId !== "000000000000000000000000" &&
                    empManagerId !== "null" &&
                    empManagerId !== "undefined";

                if (selectedManager && selectedManager.value !== 'ALL') {
                    if (selectedManager.value === 'NO_MANAGER') {
                        if (hasValidManager) return;
                    } else {
                        if (empManagerId !== selectedManager.value) return;
                    }
                }

                const managerName = hasValidManager && userMap[empManagerId]
                    ? userMap[empManagerId].name
                    : (hasValidManager ? (emp.managerName || 'Assigned Manager') : 'No Manager');

                // Search filter
                if (searchTerm.trim() !== '') {
                    const term = searchTerm.toLowerCase();
                    const matchName = (emp.name || '').toLowerCase().includes(term);
                    const matchPhone = (emp.phoneNumber || '').toString().includes(term);
                    const matchDesignation = (emp.designation || '').toLowerCase().includes(term);
                    const matchState = empState.toLowerCase().includes(term);
                    const matchManager = managerName.toLowerCase().includes(term);
                    if (!matchName && !matchPhone && !matchDesignation && !matchState && !matchManager) {
                        return;
                    }
                }

                const autoKm = getTripAutoKm(trip);
                const diffKm = parseFloat(Math.abs(manualKm - autoKm).toFixed(2));

                list.push({
                    tripId: trip.id || trip._id,
                    tripDate: trip.date || trip.startTime,
                    userId: trip.userId,
                    name: emp.name || 'Unnamed',
                    phoneNumber: emp.phoneNumber || '',
                    designation: emp.designation || 'Employee',
                    state: empState,
                    managerId: hasValidManager ? empManagerId : null,
                    managerName,
                    profileURL: emp.profileURL || '',
                    startKM: trip.startKM !== undefined ? trip.startKM : null,
                    endKM: trip.endKM !== undefined ? trip.endKM : null,
                    manualKm,
                    autoKm,
                    diffKm,
                    rawTrip: trip
                });
            }
        });

        return list.sort((a, b) => b.manualKm - a.manualKm);
    }, [trips, selectedState, selectedManager, searchTerm]);

    // Top Summary KPI Cards
    const topPerformer = bestPerformersData[0] || null;
    const mostVisitsEmp = useMemo(() => {
        if (!filteredCohort.length) return null;
        return [...filteredCohort].sort((a, b) => b.tasksCompleted - a.tasksCompleted)[0];
    }, [filteredCohort]);

    const mostDistanceEmp = useMemo(() => {
        if (!filteredCohort.length) return null;
        return [...filteredCohort].sort((a, b) => b.totalDistanceKm - a.totalDistanceKm)[0];
    }, [filteredCohort]);

    const totalNextDayTrips = useMemo(() => {
        return filteredCohort.reduce((acc, cur) => acc + cur.nextDayTripCount, 0);
    }, [filteredCohort]);

    const topExcessTrip = excessKmTripsData[0] || null;
    const uniqueExcessEmployeesCount = useMemo(() => {
        return new Set(excessKmTripsData.map(t => t.userId)).size;
    }, [excessKmTripsData]);

    // Custom React-Select styles for spacious minimal look
    const selectCustomStyles = {
        control: (base, state) => ({
            ...base,
            minHeight: '44px',
            borderRadius: '10px',
            borderColor: state.isFocused ? '#3a57e8' : '#e2e8f0',
            boxShadow: state.isFocused ? '0 0 0 3px rgba(58, 87, 232, 0.12)' : 'none',
            backgroundColor: '#ffffff',
            '&:hover': {
                borderColor: '#cbd5e1'
            },
            fontSize: '0.92rem',
            cursor: 'pointer'
        }),
        menu: (base) => ({
            ...base,
            borderRadius: '12px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04)',
            zIndex: 9999,
            border: '1px solid #e2e8f0'
        }),
        option: (base, state) => ({
            ...base,
            fontSize: '0.9rem',
            padding: '10px 14px',
            backgroundColor: state.isSelected ? '#3a57e8' : state.isFocused ? '#f1f5f9' : 'transparent',
            color: state.isSelected ? '#ffffff' : '#1e293b',
            cursor: 'pointer'
        })
    };

    // Custom DataTable styles for spacious, clean, minimal look
    const customTableStyles = {
        table: {
            style: {
                backgroundColor: '#ffffff',
            },
        },
        headRow: {
            style: {
                backgroundColor: '#f8fafc',
                minHeight: '52px',
                borderBottom: '1px solid #eef2f6',
            },
        },
        headCells: {
            style: {
                fontSize: '0.78rem',
                fontWeight: '700',
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                paddingLeft: '18px',
                paddingRight: '18px',
            },
        },
        rows: {
            style: {
                minHeight: '68px',
                fontSize: '0.93rem',
                color: '#1e293b',
                borderBottom: '1px solid #f1f5f9',
                '&:hover': {
                    backgroundColor: '#fafbfc',
                    transition: 'background-color 0.15s ease',
                },
            },
        },
        cells: {
            style: {
                paddingLeft: '18px',
                paddingRight: '18px',
            },
        },
        pagination: {
            style: {
                fontSize: '0.88rem',
                color: '#64748b',
                borderTop: '1px solid #eef2f6',
                padding: '14px 18px',
            },
        },
    };

    // Render Employee Avatar and Name Cell
    const renderEmployeeCell = (row) => (
        <div className="d-flex align-items-center py-2">
            <img
                src={row.profileURL || avatar1}
                alt={row.name}
                className="rounded-3 me-3"
                style={{ width: '40px', height: '40px', objectFit: 'cover', border: '1px solid #e2e8f0' }}
                onError={(e) => { e.target.src = avatar1; }}
            />
            <div>
                <Link
                    to={`/dashboard/users/${row.id}`}
                    className="fw-semibold text-dark text-decoration-none d-block text-truncate"
                    style={{ fontSize: '0.94rem', maxWidth: '180px' }}
                    title={row.name}
                >
                    {row.name}
                </Link>
                <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                    {row.designation} &bull; {row.phoneNumber}
                </div>
            </div>
        </div>
    );

    // Export Excel Workbook with all 4 performance sheets
    const handleExportExcel = () => {
        try {
            const wb = XLSX.utils.book_new();

            // 1. Best Performers Sheet
            const bestPerformersRows = [
                ['Rank', 'Employee Name', 'Phone', 'Designation', 'State', 'Manager', 'Overall Score (100)', 'Tasks Completed', 'Total Distance (km)', 'Days Present', 'Absent Days', 'On-Time Trips Rate %']
            ];
            bestPerformersData.forEach((r, idx) => {
                bestPerformersRows.push([
                    idx + 1,
                    r.name,
                    r.phoneNumber,
                    r.designation,
                    r.state,
                    r.managerName || 'No Manager',
                    r.bestPerformerScore,
                    r.tasksCompleted,
                    r.totalDistanceKm,
                    r.presentDays,
                    r.absentDays,
                    `${r.onTimeRate}%`
                ]);
            });
            const ws1 = XLSX.utils.aoa_to_sheet(bestPerformersRows);
            XLSX.utils.book_append_sheet(wb, ws1, 'Best Performers');

            // 2. Irregular Employees Sheet
            const irregularRows = [
                ['Rank', 'Employee Name', 'Phone', 'Designation', 'State', 'Manager', 'Absent Days', 'Leave Days', 'Present Days', 'Working Days', 'Absence Rate %']
            ];
            irregularEmployeesData.forEach((r, idx) => {
                irregularRows.push([
                    idx + 1,
                    r.name,
                    r.phoneNumber,
                    r.designation,
                    r.state,
                    r.managerName || 'No Manager',
                    r.absentDays,
                    r.leaveDays,
                    r.presentDays,
                    r.workingDays,
                    `${r.absenceRate}%`
                ]);
            });
            const ws2 = XLSX.utils.aoa_to_sheet(irregularRows);
            XLSX.utils.book_append_sheet(wb, ws2, 'Irregular Employees');

            // 3. Less Visit Employees Sheet
            const lessVisitRows = [
                ['Rank', 'Employee Name', 'Phone', 'Designation', 'State', 'Manager', 'Tasks Completed', 'Total Trips', 'Avg Visits/Trip', 'Total Distance (km)']
            ];
            lessVisitEmployeesData.forEach((r, idx) => {
                lessVisitRows.push([
                    idx + 1,
                    r.name,
                    r.phoneNumber,
                    r.designation,
                    r.state,
                    r.managerName || 'No Manager',
                    r.tasksCompleted,
                    r.totalTrips,
                    r.avgVisitsPerTrip,
                    r.totalDistanceKm
                ]);
            });
            const ws3 = XLSX.utils.aoa_to_sheet(lessVisitRows);
            XLSX.utils.book_append_sheet(wb, ws3, 'Less Visits');

            // 4. Next-Day Trip Ends Sheet
            const nextDayRows = [
                ['Rank', 'Employee Name', 'Phone', 'Designation', 'State', 'Manager', 'Next-Day Trip Ends', 'Total Trips', 'Late End Rate %', 'On-Time Trips', 'Latest Late End Date']
            ];
            nextDayTripEmployeesData.forEach((r, idx) => {
                nextDayRows.push([
                    idx + 1,
                    r.name,
                    r.phoneNumber,
                    r.designation,
                    r.state,
                    r.managerName || 'No Manager',
                    r.nextDayTripCount,
                    r.totalTrips,
                    `${r.lateEndRate}%`,
                    r.onTimeTrips,
                    r.latestLateEndTs ? moment(r.latestLateEndTs).format('DD/MM/YYYY hh:mm A') : 'N/A'
                ]);
            });
            const ws4 = XLSX.utils.aoa_to_sheet(nextDayRows);
            XLSX.utils.book_append_sheet(wb, ws4, 'Next-Day Trip Ends');

            // 5. Excess KM Alerts Sheet (> 1000 KM)
            const excessKmRows = [
                ['Rank', 'Employee Name', 'Phone', 'Designation', 'State', 'Manager', 'Trip Date', 'Manual KM', 'Auto Calculated KM', 'KM Difference', 'Start KM', 'End KM', 'Trip ID']
            ];
            excessKmTripsData.forEach((r, idx) => {
                excessKmRows.push([
                    idx + 1,
                    r.name,
                    r.phoneNumber,
                    r.designation,
                    r.state,
                    r.managerName || 'No Manager',
                    r.tripDate ? moment(r.tripDate).format('DD/MM/YYYY') : '-',
                    r.manualKm,
                    r.autoKm,
                    r.diffKm,
                    r.startKM !== null ? r.startKM : '-',
                    r.endKM !== null ? r.endKM : '-',
                    r.tripId || '-'
                ]);
            });
            const ws5 = XLSX.utils.aoa_to_sheet(excessKmRows);
            XLSX.utils.book_append_sheet(wb, ws5, 'Excess KM Alerts');

            const fileName = `Performance_Analysis_${fromDate}_to_${toDate}.xlsx`;
            XLSX.writeFile(wb, fileName);
            toast.success("Performance report downloaded successfully!");
        } catch (err) {
            console.error("Failed to generate excel file", err);
            toast.error("Failed to export Excel report");
        }
    };

    // Columns: Best Performers Leaderboard
    const bestPerformerColumns = [
        {
            name: 'Rank',
            width: '90px',
            center: true,
            selector: (row, i) => i + 1,
            cell: (row, index) => {
                if (index === 0) return <span className="badge rounded-pill" style={{ background: '#fef3c7', color: '#b45309', fontWeight: 700, fontSize: '0.85rem', padding: '6px 12px' }}>🥇 #1</span>;
                if (index === 1) return <span className="badge rounded-pill" style={{ background: '#f1f5f9', color: '#475569', fontWeight: 600, fontSize: '0.85rem', padding: '6px 12px' }}>🥈 #2</span>;
                if (index === 2) return <span className="badge rounded-pill" style={{ background: '#ffedd5', color: '#9a3412', fontWeight: 600, fontSize: '0.85rem', padding: '6px 12px' }}>🥉 #3</span>;
                return <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: '0.92rem' }}>#{index + 1}</span>;
            }
        },
        {
            name: 'Employee',
            width: '260px',
            selector: row => row.name,
            sortable: true,
            cell: renderEmployeeCell
        },
        {
            name: 'State',
            width: '130px',
            selector: row => row.state,
            sortable: true,
            cell: row => (
                <span className="badge" style={{ background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '5px 10px', fontSize: '0.82rem', fontWeight: 500 }}>
                    {row.state}
                </span>
            )
        },
        {
            name: 'Manager',
            width: '140px',
            selector: row => row.managerName,
            sortable: true,
            cell: row => (
                <span
                    className="badge text-truncate"
                    title={row.managerName}
                    style={{
                        background: (row.managerName && row.managerName !== 'No Manager' && row.managerName !== 'Unassigned') ? '#f0fdf4' : '#f8fafc',
                        color: (row.managerName && row.managerName !== 'No Manager' && row.managerName !== 'Unassigned') ? '#15803d' : '#64748b',
                        border: (row.managerName && row.managerName !== 'No Manager' && row.managerName !== 'Unassigned') ? '1px solid #bbf7d0' : '1px solid #e2e8f0',
                        borderRadius: '8px',
                        padding: '5px 10px',
                        fontSize: '0.82rem',
                        fontWeight: 500,
                        maxWidth: '130px'
                    }}
                >
                    {row.managerName || 'No Manager'}
                </span>
            )
        },
        {
            name: 'Overall Score',
            width: '170px',
            center: true,
            selector: row => row.bestPerformerScore,
            sortable: true,
            cell: row => (
                <div style={{ width: '130px' }}>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                        <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.94rem' }}>{row.bestPerformerScore}</span>
                        <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>/ 100</span>
                    </div>
                    <div className="progress" style={{ height: '6px', borderRadius: '6px', background: '#f1f5f9' }}>
                        <div
                            className="progress-bar"
                            style={{
                                width: `${row.bestPerformerScore}%`,
                                background: row.bestPerformerScore >= 75 ? '#10b981' : row.bestPerformerScore >= 40 ? '#3b82f6' : '#f59e0b',
                                borderRadius: '6px'
                            }}
                        ></div>
                    </div>
                </div>
            )
        },
        {
            name: 'Tasks Completed',
            center: true,
            selector: row => row.tasksCompleted,
            sortable: true,
            cell: row => (
                <span className="badge" style={{ background: row.tasksCompleted > 0 ? '#ecfdf5' : '#f8fafc', color: row.tasksCompleted > 0 ? '#059669' : '#94a3b8', border: row.tasksCompleted > 0 ? '1px solid #d1fae5' : '1px solid #e2e8f0', borderRadius: '20px', padding: '6px 14px', fontSize: '0.86rem', fontWeight: 600 }}>
                    <i className="fas fa-check-circle me-1"></i>{row.tasksCompleted}
                </span>
            )
        },
        {
            name: 'Distance (KM)',
            center: true,
            selector: row => row.totalDistanceKm,
            sortable: true,
            cell: row => <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.94rem' }}>{row.totalDistanceKm} km</span>
        },
        {
            name: 'Days Present',
            center: true,
            selector: row => row.presentDays,
            sortable: true,
            cell: row => (
                <span className="badge" style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #dbeafe', borderRadius: '16px', padding: '6px 12px', fontSize: '0.84rem', fontWeight: 500 }}>
                    {row.presentDays} / {row.workingDays} days
                </span>
            )
        },
        {
            name: 'On-Time Trips %',
            center: true,
            selector: row => row.onTimeRate,
            sortable: true,
            cell: row => (
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>{row.onTimeTrips} on-time trips out of {row.totalTrips} total trips</Tooltip>}
                >
                    <span className="badge" style={{ background: row.onTimeRate >= 80 ? '#ecfdf5' : '#fef2f2', color: row.onTimeRate >= 80 ? '#059669' : '#dc2626', border: row.onTimeRate >= 80 ? '1px solid #d1fae5' : '1px solid #fee2e2', borderRadius: '16px', padding: '6px 12px', fontSize: '0.84rem', fontWeight: 600 }}>
                        {row.onTimeRate}%
                    </span>
                </OverlayTrigger>
            )
        },
        {
            name: 'Absent Days',
            center: true,
            selector: row => row.absentDays,
            sortable: true,
            cell: row => row.absentDays > 0
                ? <span className="badge" style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fee2e2', borderRadius: '12px', padding: '5px 10px', fontWeight: 600, fontSize: '0.84rem' }}>{row.absentDays}</span>
                : <span style={{ color: '#94a3b8', fontSize: '0.9rem' }}>0</span>
        }
    ];

    // Columns: Irregular Employees
    const irregularColumns = [
        {
            name: 'Rank',
            width: '90px',
            center: true,
            cell: (row, index) => <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: '0.92rem' }}>#{index + 1}</span>
        },
        {
            name: 'Employee',
            width: '260px',
            selector: row => row.name,
            sortable: true,
            cell: renderEmployeeCell
        },
        {
            name: 'State',
            width: '140px',
            selector: row => row.state,
            sortable: true,
            cell: row => (
                <span className="badge" style={{ background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '5px 10px', fontSize: '0.82rem', fontWeight: 500 }}>
                    {row.state}
                </span>
            )
        },
        {
            name: 'Manager',
            width: '140px',
            selector: row => row.managerName,
            sortable: true,
            cell: row => (
                <span
                    className="badge text-truncate"
                    title={row.managerName}
                    style={{
                        background: (row.managerName && row.managerName !== 'No Manager' && row.managerName !== 'Unassigned') ? '#f0fdf4' : '#f8fafc',
                        color: (row.managerName && row.managerName !== 'No Manager' && row.managerName !== 'Unassigned') ? '#15803d' : '#64748b',
                        border: (row.managerName && row.managerName !== 'No Manager' && row.managerName !== 'Unassigned') ? '1px solid #bbf7d0' : '1px solid #e2e8f0',
                        borderRadius: '8px',
                        padding: '5px 10px',
                        fontSize: '0.82rem',
                        fontWeight: 500,
                        maxWidth: '130px'
                    }}
                >
                    {row.managerName || 'No Manager'}
                </span>
            )
        },
        {
            name: 'Absent Days',
            center: true,
            selector: row => row.absentDays,
            sortable: true,
            cell: row => (
                <span className="badge" style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fee2e2', borderRadius: '16px', padding: '6px 14px', fontSize: '0.86rem', fontWeight: 600 }}>
                    <i className="fas fa-exclamation-triangle me-1"></i>{row.absentDays} {row.absentDays === 1 ? 'Day' : 'Days'}
                </span>
            )
        },
        {
            name: 'Leave Days',
            center: true,
            selector: row => row.leaveDays,
            sortable: true,
            cell: row => (
                <span className="badge" style={{ background: '#fffbeb', color: '#b45309', border: '1px solid #fef3c7', borderRadius: '16px', padding: '6px 12px', fontSize: '0.84rem', fontWeight: 500 }}>
                    {row.leaveDays} Leaves
                </span>
            )
        },
        {
            name: 'Present Days',
            center: true,
            selector: row => row.presentDays,
            sortable: true,
            cell: row => (
                <span className="badge" style={{ background: '#ecfdf5', color: '#059669', border: '1px solid #d1fae5', borderRadius: '16px', padding: '6px 12px', fontSize: '0.84rem', fontWeight: 500 }}>
                    {row.presentDays} Present
                </span>
            )
        },
        {
            name: 'Working Days',
            center: true,
            selector: row => row.workingDays,
            sortable: true,
            cell: row => <span style={{ color: '#64748b', fontSize: '0.92rem' }}>{row.workingDays}</span>
        },
        {
            name: 'Absence Rate',
            center: true,
            selector: row => row.absenceRate,
            sortable: true,
            cell: row => (
                <span className="badge" style={{ background: row.absenceRate > 30 ? '#fef2f2' : row.absenceRate > 15 ? '#fffbeb' : '#f8fafc', color: row.absenceRate > 30 ? '#dc2626' : row.absenceRate > 15 ? '#b45309' : '#64748b', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '6px 12px', fontSize: '0.84rem', fontWeight: 600 }}>
                    {row.absenceRate}% Absent
                </span>
            )
        }
    ];

    // Columns: Less Visit Employees
    const lessVisitColumns = [
        {
            name: 'Rank',
            width: '90px',
            center: true,
            cell: (row, index) => <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: '0.92rem' }}>#{index + 1}</span>
        },
        {
            name: 'Employee',
            width: '260px',
            selector: row => row.name,
            sortable: true,
            cell: renderEmployeeCell
        },
        {
            name: 'State',
            width: '140px',
            selector: row => row.state,
            sortable: true,
            cell: row => (
                <span className="badge" style={{ background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '5px 10px', fontSize: '0.82rem', fontWeight: 500 }}>
                    {row.state}
                </span>
            )
        },
        {
            name: 'Manager',
            width: '140px',
            selector: row => row.managerName,
            sortable: true,
            cell: row => (
                <span
                    className="badge text-truncate"
                    title={row.managerName}
                    style={{
                        background: (row.managerName && row.managerName !== 'No Manager' && row.managerName !== 'Unassigned') ? '#f0fdf4' : '#f8fafc',
                        color: (row.managerName && row.managerName !== 'No Manager' && row.managerName !== 'Unassigned') ? '#15803d' : '#64748b',
                        border: (row.managerName && row.managerName !== 'No Manager' && row.managerName !== 'Unassigned') ? '1px solid #bbf7d0' : '1px solid #e2e8f0',
                        borderRadius: '8px',
                        padding: '5px 10px',
                        fontSize: '0.82rem',
                        fontWeight: 500,
                        maxWidth: '130px'
                    }}
                >
                    {row.managerName || 'No Manager'}
                </span>
            )
        },
        {
            name: 'Tasks / Visits Completed',
            center: true,
            selector: row => row.tasksCompleted,
            sortable: true,
            cell: row => (
                <span className="badge" style={{ background: row.tasksCompleted === 0 ? '#fef2f2' : row.tasksCompleted < 5 ? '#fffbeb' : '#eff6ff', color: row.tasksCompleted === 0 ? '#dc2626' : row.tasksCompleted < 5 ? '#b45309' : '#2563eb', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '6px 14px', fontSize: '0.86rem', fontWeight: 600 }}>
                    {row.tasksCompleted} {row.tasksCompleted === 1 ? 'Visit' : 'Visits'}
                </span>
            )
        },
        {
            name: 'Total Trips Started',
            center: true,
            selector: row => row.totalTrips,
            sortable: true,
            cell: row => <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.92rem' }}>{row.totalTrips}</span>
        },
        {
            name: 'Avg Visits / Trip',
            center: true,
            selector: row => row.avgVisitsPerTrip,
            sortable: true,
            cell: row => <span style={{ color: '#64748b', fontSize: '0.92rem' }}>{row.avgVisitsPerTrip}</span>
        },
        {
            name: 'Total Distance',
            center: true,
            selector: row => row.totalDistanceKm,
            sortable: true,
            cell: row => <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.92rem' }}>{row.totalDistanceKm} km</span>
        }
    ];

    // Columns: Next-Day Trip End Employees
    const nextDayTripColumns = [
        {
            name: 'Rank',
            width: '90px',
            center: true,
            cell: (row, index) => <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: '0.92rem' }}>#{index + 1}</span>
        },
        {
            name: 'Employee',
            width: '260px',
            selector: row => row.name,
            sortable: true,
            cell: renderEmployeeCell
        },
        {
            name: 'State',
            width: '140px',
            selector: row => row.state,
            sortable: true,
            cell: row => (
                <span className="badge" style={{ background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '5px 10px', fontSize: '0.82rem', fontWeight: 500 }}>
                    {row.state}
                </span>
            )
        },
        {
            name: 'Manager',
            width: '140px',
            selector: row => row.managerName,
            sortable: true,
            cell: row => (
                <span
                    className="badge text-truncate"
                    title={row.managerName}
                    style={{
                        background: (row.managerName && row.managerName !== 'No Manager' && row.managerName !== 'Unassigned') ? '#f0fdf4' : '#f8fafc',
                        color: (row.managerName && row.managerName !== 'No Manager' && row.managerName !== 'Unassigned') ? '#15803d' : '#64748b',
                        border: (row.managerName && row.managerName !== 'No Manager' && row.managerName !== 'Unassigned') ? '1px solid #bbf7d0' : '1px solid #e2e8f0',
                        borderRadius: '8px',
                        padding: '5px 10px',
                        fontSize: '0.82rem',
                        fontWeight: 500,
                        maxWidth: '130px'
                    }}
                >
                    {row.managerName || 'No Manager'}
                </span>
            )
        },
        {
            name: 'Next-Day Trip Ends',
            center: true,
            selector: row => row.nextDayTripCount,
            sortable: true,
            cell: row => (
                <span className="badge" style={{ background: row.nextDayTripCount > 0 ? '#fef2f2' : '#ecfdf5', color: row.nextDayTripCount > 0 ? '#dc2626' : '#059669', border: row.nextDayTripCount > 0 ? '1px solid #fee2e2' : '1px solid #d1fae5', borderRadius: '16px', padding: '6px 14px', fontSize: '0.86rem', fontWeight: 600 }}>
                    <i className="fas fa-moon me-1"></i>{row.nextDayTripCount} {row.nextDayTripCount === 1 ? 'Trip' : 'Trips'}
                </span>
            )
        },
        {
            name: 'Total Trips',
            center: true,
            selector: row => row.totalTrips,
            sortable: true,
            cell: row => <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.92rem' }}>{row.totalTrips}</span>
        },
        {
            name: 'Late Trip End %',
            center: true,
            selector: row => row.lateEndRate,
            sortable: true,
            cell: row => (
                <span className="badge" style={{ background: row.lateEndRate > 25 ? '#fef2f2' : row.lateEndRate > 0 ? '#fffbeb' : '#ecfdf5', color: row.lateEndRate > 25 ? '#dc2626' : row.lateEndRate > 0 ? '#b45309' : '#059669', borderRadius: '16px', padding: '6px 12px', fontSize: '0.84rem', fontWeight: 600 }}>
                    {row.lateEndRate}% of trips
                </span>
            )
        },
        {
            name: 'On-Time Trips',
            center: true,
            selector: row => row.onTimeTrips,
            sortable: true,
            cell: row => <span style={{ fontWeight: 600, color: '#059669', fontSize: '0.92rem' }}>{row.onTimeTrips}</span>
        },
        {
            name: 'Latest Late End',
            center: true,
            selector: row => row.latestLateEndTs || 0,
            sortable: true,
            cell: row => row.latestLateEndTs
                ? <small style={{ color: '#64748b', fontSize: '0.82rem' }}>{moment(row.latestLateEndTs).format('DD/MM/YY hh:mm A')}</small>
                : <span style={{ color: '#94a3b8' }}>-</span>
        }
    ];

    // Columns: Excess KM Trips (> 1000 KM)
    const excessKmColumns = [
        {
            name: 'Rank',
            width: '80px',
            center: true,
            cell: (row, index) => <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: '0.92rem' }}>#{index + 1}</span>
        },
        {
            name: 'Employee',
            width: '260px',
            selector: row => row.name,
            sortable: true,
            cell: renderEmployeeCell
        },
        {
            name: 'State',
            width: '130px',
            selector: row => row.state,
            sortable: true,
            cell: row => (
                <span className="badge" style={{ background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '5px 10px', fontSize: '0.82rem', fontWeight: 500 }}>
                    {row.state}
                </span>
            )
        },
        {
            name: 'Manager',
            width: '140px',
            selector: row => row.managerName,
            sortable: true,
            cell: row => (
                <span
                    className="badge text-truncate"
                    title={row.managerName}
                    style={{
                        background: (row.managerName && row.managerName !== 'No Manager' && row.managerName !== 'Unassigned') ? '#f0fdf4' : '#f8fafc',
                        color: (row.managerName && row.managerName !== 'No Manager' && row.managerName !== 'Unassigned') ? '#15803d' : '#64748b',
                        border: (row.managerName && row.managerName !== 'No Manager' && row.managerName !== 'Unassigned') ? '1px solid #bbf7d0' : '1px solid #e2e8f0',
                        borderRadius: '8px',
                        padding: '5px 10px',
                        fontSize: '0.82rem',
                        fontWeight: 500,
                        maxWidth: '130px'
                    }}
                >
                    {row.managerName || 'No Manager'}
                </span>
            )
        },
        {
            name: 'Trip Date',
            width: '130px',
            center: true,
            selector: row => row.tripDate || 0,
            sortable: true,
            cell: row => row.tripDate ? <span style={{ color: '#334155', fontWeight: 500, fontSize: '0.88rem' }}>{moment(row.tripDate).format('DD/MM/YYYY')}</span> : '-'
        },
        {
            name: 'Manual KM',
            width: '160px',
            center: true,
            selector: row => row.manualKm,
            sortable: true,
            cell: row => (
                <span className="badge" style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '16px', padding: '6px 14px', fontSize: '0.86rem', fontWeight: 700 }}>
                    <i className="fas fa-exclamation-triangle me-1"></i>{row.manualKm.toLocaleString()} km
                </span>
            )
        },
        {
            name: 'Auto KM',
            width: '120px',
            center: true,
            selector: row => row.autoKm,
            sortable: true,
            cell: row => <span style={{ color: '#64748b', fontWeight: 500, fontSize: '0.9rem' }}>{row.autoKm} km</span>
        },
        {
            name: 'Difference',
            width: '140px',
            center: true,
            selector: row => row.diffKm,
            sortable: true,
            cell: row => (
                <span style={{ color: '#dc2626', fontWeight: 700, fontSize: '0.92rem' }}>
                    +{row.diffKm.toLocaleString()} km
                </span>
            )
        },
        {
            name: 'Odometer (Start / End)',
            width: '180px',
            center: true,
            cell: row => (
                <small style={{ color: '#64748b', fontSize: '0.82rem' }}>
                    {row.startKM !== null ? `${row.startKM}` : '-'} &rarr; {row.endKM !== null ? `${row.endKM}` : '-'}
                </small>
            )
        },
        {
            name: 'Action',
            width: '190px',
            center: true,
            cell: row => (
                <div className="d-flex align-items-center gap-2">
                    <Button
                        size="sm"
                        variant="primary"
                        style={{
                            borderRadius: '10px',
                            fontWeight: 600,
                            fontSize: '0.82rem',
                            padding: '6px 12px',
                            boxShadow: 'none'
                        }}
                        onClick={() => {
                            setSelectedTripToEdit(row.rawTrip);
                            setShowEditTripModal(true);
                        }}
                        title="Update Trip Start/End KM"
                    >
                        <i className="fas fa-edit me-1"></i> Update Trip
                    </Button>
                    <Link
                        to={`/dashboard/trips/${row.tripId}`}
                        target="_blank"
                        className="btn btn-sm btn-outline-secondary"
                        style={{
                            borderRadius: '10px',
                            padding: '6px 10px',
                            fontSize: '0.82rem'
                        }}
                        title="View Trip Details"
                    >
                        <i className="fas fa-external-link-alt"></i>
                    </Link>
                </div>
            )
        }
    ];

    return (
        <div className="performance-analysis-module py-3">
            {/* Top Header Card */}
            <Card className="mb-4 border-0" style={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(15,23,42,0.04), 0 8px 24px -4px rgba(15,23,42,0.04)' }}>
                <Card.Body className="p-4">
                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                        <div>
                            <h3 className="fw-bold mb-1" style={{ color: '#0f172a', fontSize: '1.55rem', letterSpacing: '-0.3px' }}>
                                Performance Analysis
                            </h3>
                            <p className="mb-0" style={{ color: '#64748b', fontSize: '0.94rem' }}>
                                Comprehensive analysis of top performers, attendance regularity, dealer visits, and route compliance.
                            </p>
                        </div>

                        {/* Top Action Buttons */}
                        <div className="d-flex flex-wrap align-items-center gap-2">
                            <Button
                                variant="light"
                                size="sm"
                                onClick={handleExportExcel}
                                disabled={isLoading || filteredCohort.length === 0}
                                style={{
                                    borderRadius: '10px',
                                    border: '1px solid #e2e8f0',
                                    backgroundColor: '#ffffff',
                                    color: '#059669',
                                    fontWeight: 600,
                                    fontSize: '0.86rem',
                                    padding: '8px 16px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                                }}
                            >
                                <i className="fas fa-file-excel"></i> Export to Excel
                            </Button>
                            <Button
                                variant="light"
                                size="sm"
                                onClick={fetchData}
                                disabled={isLoading}
                                style={{
                                    borderRadius: '10px',
                                    border: '1px solid #e2e8f0',
                                    backgroundColor: '#ffffff',
                                    color: '#3b82f6',
                                    fontWeight: 600,
                                    fontSize: '0.86rem',
                                    padding: '8px 16px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                                }}
                            >
                                <i className={`fas fa-sync-alt ${isLoading ? 'fa-spin' : ''}`}></i> Refresh
                            </Button>
                        </div>
                    </div>

                    {/* Filter Bar */}
                    <Row className="g-3 align-items-center pt-3 border-top" style={{ borderColor: '#f1f5f9' }}>
                        {/* Period Filter Buttons: Segmented control */}
                        <Col xl={3} lg={6} md={6} sm={12}>
                            <label className="d-block mb-1" style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>
                                <i className="fas fa-calendar-alt me-1"></i> Analysis Period
                            </label>
                            <div
                                style={{
                                    background: '#f8fafc',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '10px',
                                    padding: '3px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '3px',
                                    height: '42px'
                                }}
                            >
                                {[
                                    { id: 'CURRENT_MONTH', label: 'Current Month' },
                                    { id: '1_MONTH', label: '1 Month' },
                                    { id: '3_MONTHS', label: '3 Months' }
                                ].map(p => {
                                    const isSelected = periodType === p.id;
                                    return (
                                        <button
                                            key={p.id}
                                            type="button"
                                            onClick={() => handlePeriodChange(p.id)}
                                            disabled={isLoading}
                                            style={{
                                                flex: 1,
                                                height: '100%',
                                                border: 'none',
                                                borderRadius: '8px',
                                                fontSize: '0.82rem',
                                                fontWeight: isSelected ? 600 : 500,
                                                background: isSelected ? '#ffffff' : 'transparent',
                                                color: isSelected ? '#0f172a' : '#64748b',
                                                boxShadow: isSelected ? '0 1px 3px rgba(15,23,42,0.08)' : 'none',
                                                cursor: 'pointer',
                                                transition: 'all 0.15s ease',
                                                whiteSpace: 'nowrap',
                                                padding: '0 4px'
                                            }}
                                        >
                                            {p.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </Col>

                        {/* State Filter */}
                        <Col xl={2} lg={3} md={6} sm={12}>
                            <label className="d-block mb-1" style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>
                                <i className="fas fa-map-marker-alt me-1"></i> State Filter
                            </label>
                            <Select
                                options={stateFilterOptions}
                                value={selectedState}
                                onChange={setSelectedState}
                                isSearchable
                                placeholder="All States"
                                styles={selectCustomStyles}
                                isDisabled={isLoading}
                            />
                        </Col>

                        {/* Manager Filter */}
                        <Col xl={3} lg={3} md={6} sm={12}>
                            <label className="d-block mb-1" style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>
                                <i className="fas fa-user-tie me-1"></i> Manager Filter
                            </label>
                            <Select
                                options={managerFilterOptions}
                                value={selectedManager}
                                onChange={setSelectedManager}
                                isSearchable
                                placeholder="All Managers"
                                styles={selectCustomStyles}
                                isDisabled={isLoading}
                            />
                        </Col>

                        {/* Search Input */}
                        <Col xl={4} lg={12} md={12} sm={12}>
                            <label className="d-block mb-1" style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>
                                <i className="fas fa-search me-1"></i> Search Employee
                            </label>
                            <div style={{ position: 'relative' }}>
                                <i
                                    className="fas fa-search"
                                    style={{
                                        position: 'absolute',
                                        left: '14px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: '#94a3b8',
                                        fontSize: '0.88rem'
                                    }}
                                ></i>
                                <Form.Control
                                    type="text"
                                    placeholder="Search by name, designation, state, or manager..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    disabled={isLoading}
                                    style={{
                                        height: '42px',
                                        borderRadius: '10px',
                                        borderColor: '#e2e8f0',
                                        paddingLeft: '38px',
                                        fontSize: '0.88rem',
                                        backgroundColor: '#ffffff'
                                    }}
                                />
                            </div>
                        </Col>
                    </Row>

                    {/* Active date range display badge */}
                    <div className="mt-3 pt-2 d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ fontSize: '0.83rem', color: '#64748b' }}>
                        <div>
                            Showing records from <strong style={{ color: '#0f172a' }}>{moment(fromDate).format('DD MMM YYYY')}</strong> to <strong style={{ color: '#0f172a' }}>{moment(toDate).format('DD MMM YYYY')}</strong>
                        </div>
                        <div className="d-flex align-items-center flex-wrap gap-2">
                            {selectedState && selectedState.value !== 'ALL' && (
                                <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '3px 10px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 500 }}>
                                    <i className="fas fa-map-marker-alt me-1"></i>{selectedState.value}
                                </span>
                            )}
                            {selectedManager && selectedManager.value !== 'ALL' && (
                                <span style={{ background: '#ecfdf5', color: '#059669', padding: '3px 10px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 500 }}>
                                    <i className="fas fa-user-tie me-1"></i>{selectedManager.value === 'NO_MANAGER' ? 'No Manager' : (selectedManager.label ? selectedManager.label.split(' (')[0] : selectedManager.value)}
                                </span>
                            )}
                            <div>
                                Total Filtered Employees: <strong style={{ color: '#0f172a', background: '#f1f5f9', padding: '3px 10px', borderRadius: '12px' }}>{filteredCohort.length}</strong>
                            </div>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            {/* Quick KPI Overview Cards */}
            <Row className="mb-4 g-3 row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-xl-5">
                {/* 1. Top Performer */}
                <Col>
                    <div
                        style={{
                            background: '#ffffff',
                            borderRadius: '16px',
                            border: '1px solid #f1f5f9',
                            boxShadow: '0 2px 8px -2px rgba(15, 23, 42, 0.04)',
                            padding: '1.25rem',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                        onClick={() => setActiveTab('bestPerformers')}
                        title="Click to view Best Performers"
                    >
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <span style={{ fontSize: '0.74rem', fontWeight: 600, color: '#64748b', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                                Top Performer
                            </span>
                            <div
                                style={{
                                    width: '38px',
                                    height: '38px',
                                    borderRadius: '10px',
                                    backgroundColor: '#ecfdf5',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#059669',
                                    fontSize: '0.95rem'
                                }}
                            >
                                <i className="fas fa-trophy"></i>
                            </div>
                        </div>
                        <div>
                            <h5 className="fw-bold mb-2 text-truncate" style={{ color: '#0f172a', fontSize: '1.08rem' }} title={topPerformer ? topPerformer.name : '-'}>
                                {topPerformer ? topPerformer.name : '-'}
                            </h5>
                            <span style={{ background: '#ecfdf5', color: '#047857', padding: '3px 10px', borderRadius: '12px', fontSize: '0.78rem', fontWeight: 600, display: 'inline-block' }}>
                                Score: {topPerformer ? `${topPerformer.bestPerformerScore}/100` : '-'}
                            </span>
                        </div>
                    </div>
                </Col>

                {/* 2. Most Dealer Visits */}
                <Col>
                    <div
                        style={{
                            background: '#ffffff',
                            borderRadius: '16px',
                            border: '1px solid #f1f5f9',
                            boxShadow: '0 2px 8px -2px rgba(15, 23, 42, 0.04)',
                            padding: '1.25rem',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                        onClick={() => setActiveTab('bestPerformers')}
                        title="Click to view Dealer Visits"
                    >
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <span style={{ fontSize: '0.74rem', fontWeight: 600, color: '#64748b', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                                Most Dealer Visits
                            </span>
                            <div
                                style={{
                                    width: '38px',
                                    height: '38px',
                                    borderRadius: '10px',
                                    backgroundColor: '#eff6ff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#2563eb',
                                    fontSize: '0.95rem'
                                }}
                            >
                                <i className="fas fa-store"></i>
                            </div>
                        </div>
                        <div>
                            <h5 className="fw-bold mb-2 text-truncate" style={{ color: '#0f172a', fontSize: '1.08rem' }} title={mostVisitsEmp ? mostVisitsEmp.name : '-'}>
                                {mostVisitsEmp ? mostVisitsEmp.name : '-'}
                            </h5>
                            <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '3px 10px', borderRadius: '12px', fontSize: '0.78rem', fontWeight: 600, display: 'inline-block' }}>
                                {mostVisitsEmp ? `${mostVisitsEmp.tasksCompleted} Visits` : '-'}
                            </span>
                        </div>
                    </div>
                </Col>

                {/* 3. Most Distance */}
                <Col>
                    <div
                        style={{
                            background: '#ffffff',
                            borderRadius: '16px',
                            border: '1px solid #f1f5f9',
                            boxShadow: '0 2px 8px -2px rgba(15, 23, 42, 0.04)',
                            padding: '1.25rem',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                        onClick={() => setActiveTab('bestPerformers')}
                        title="Click to view Distance Traveled"
                    >
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <span style={{ fontSize: '0.74rem', fontWeight: 600, color: '#64748b', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                                Most Distance
                            </span>
                            <div
                                style={{
                                    width: '38px',
                                    height: '38px',
                                    borderRadius: '10px',
                                    backgroundColor: '#fffbeb',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#d97706',
                                    fontSize: '0.95rem'
                                }}
                            >
                                <i className="fas fa-route"></i>
                            </div>
                        </div>
                        <div>
                            <h5 className="fw-bold mb-2 text-truncate" style={{ color: '#0f172a', fontSize: '1.08rem' }} title={mostDistanceEmp ? mostDistanceEmp.name : '-'}>
                                {mostDistanceEmp ? mostDistanceEmp.name : '-'}
                            </h5>
                            <span style={{ background: '#fffbeb', color: '#b45309', padding: '3px 10px', borderRadius: '12px', fontSize: '0.78rem', fontWeight: 600, display: 'inline-block' }}>
                                {mostDistanceEmp ? `${mostDistanceEmp.totalDistanceKm} KM` : '-'}
                            </span>
                        </div>
                    </div>
                </Col>

                {/* 4. Next-Day Trip Ends */}
                <Col>
                    <div
                        style={{
                            background: '#ffffff',
                            borderRadius: '16px',
                            border: '1px solid #f1f5f9',
                            boxShadow: '0 2px 8px -2px rgba(15, 23, 42, 0.04)',
                            padding: '1.25rem',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                        onClick={() => setActiveTab('nextDayTrips')}
                        title="Click to view Next-Day Trip Ends"
                    >
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <span style={{ fontSize: '0.74rem', fontWeight: 600, color: '#64748b', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                                Next-Day Trips
                            </span>
                            <div
                                style={{
                                    width: '38px',
                                    height: '38px',
                                    borderRadius: '10px',
                                    backgroundColor: '#fef2f2',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#dc2626',
                                    fontSize: '0.95rem'
                                }}
                            >
                                <i className="fas fa-moon"></i>
                            </div>
                        </div>
                        <div>
                            <h5 className="fw-bold mb-2" style={{ color: totalNextDayTrips > 0 ? '#dc2626' : '#0f172a', fontSize: '1.08rem' }}>
                                {totalNextDayTrips} Trips
                            </h5>
                            <span style={{ background: '#f8fafc', color: '#64748b', padding: '3px 10px', borderRadius: '12px', fontSize: '0.78rem', fontWeight: 500, display: 'inline-block' }}>
                                Ended after 11:59 PM
                            </span>
                        </div>
                    </div>
                </Col>

                {/* 5. Excess KM Alert Card */}
                <Col>
                    <div
                        style={{
                            background: '#ffffff',
                            borderRadius: '16px',
                            border: '1px solid #f1f5f9',
                            boxShadow: '0 2px 8px -2px rgba(15, 23, 42, 0.04)',
                            padding: '1.25rem',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                        onClick={() => setActiveTab('excessKmTrips')}
                        title="Click to view Excess KM Trips (>1000 KM)"
                    >
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <span style={{ fontSize: '0.74rem', fontWeight: 600, color: '#64748b', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                                Excess KM Alert
                            </span>
                            <div
                                style={{
                                    width: '38px',
                                    height: '38px',
                                    borderRadius: '10px',
                                    backgroundColor: excessKmTripsData.length > 0 ? '#fef2f2' : '#ecfdf5',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: excessKmTripsData.length > 0 ? '#dc2626' : '#059669',
                                    fontSize: '0.95rem'
                                }}
                            >
                                <i className={`fas ${excessKmTripsData.length > 0 ? 'fa-exclamation-triangle' : 'fa-check'}`}></i>
                            </div>
                        </div>
                        <div>
                            <h5
                                className="fw-bold mb-2 text-truncate"
                                style={{ color: excessKmTripsData.length > 0 ? '#dc2626' : '#0f172a', fontSize: '1.08rem' }}
                                title={topExcessTrip ? `${topExcessTrip.name} (${topExcessTrip.manualKm.toLocaleString()} KM)` : 'No Excess KM'}
                            >
                                {topExcessTrip ? topExcessTrip.name : 'No Excess KM'}
                            </h5>
                            <span
                                style={{
                                    background: excessKmTripsData.length > 0 ? '#fef2f2' : '#ecfdf5',
                                    color: excessKmTripsData.length > 0 ? '#b91c1c' : '#047857',
                                    padding: '3px 10px',
                                    borderRadius: '12px',
                                    fontSize: '0.78rem',
                                    fontWeight: 600,
                                    display: 'inline-block'
                                }}
                            >
                                {excessKmTripsData.length > 0
                                    ? `${topExcessTrip.manualKm.toLocaleString()} KM (${excessKmTripsData.length} ${excessKmTripsData.length === 1 ? 'Trip' : 'Trips'} / ${uniqueExcessEmployeesCount} ${uniqueExcessEmployeesCount === 1 ? 'Emp' : 'Emps'})`
                                    : 'All Trips < 1000 KM'}
                            </span>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Main Tabs and Tables Card */}
            <Card className="border-0" style={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(15,23,42,0.04), 0 8px 24px -4px rgba(15,23,42,0.04)', overflow: 'hidden' }}>
                <div
                    className="p-3 bg-white border-bottom d-flex align-items-center gap-2"
                    style={{
                        overflowX: 'auto',
                        whiteSpace: 'nowrap',
                        borderColor: '#f1f5f9'
                    }}
                >
                    {[
                        {
                            key: 'bestPerformers',
                            icon: 'fa-trophy',
                            label: 'Leaderboard',
                            count: bestPerformersData.length,
                            color: '#d97706',
                            badgeBg: '#fef3c7',
                            badgeColor: '#b45309'
                        },
                        {
                            key: 'irregularEmployees',
                            icon: 'fa-user-clock',
                            label: 'Irregular Attendance',
                            count: irregularEmployeesData.filter(e => e.absentDays > 0).length,
                            color: '#dc2626',
                            badgeBg: '#fee2e2',
                            badgeColor: '#b91c1c'
                        },
                        {
                            key: 'lessVisits',
                            icon: 'fa-store-slash',
                            label: 'Low Visits',
                            count: lessVisitEmployeesData.filter(e => e.tasksCompleted === 0).length,
                            color: '#f59e0b',
                            badgeBg: '#fef3c7',
                            badgeColor: '#b45309'
                        },
                        {
                            key: 'nextDayTrips',
                            icon: 'fa-moon',
                            label: 'Next-Day Trips',
                            count: nextDayTripEmployeesData.filter(e => e.nextDayTripCount > 0).length,
                            color: '#6366f1',
                            badgeBg: '#e0e7ff',
                            badgeColor: '#4338ca'
                        },
                        {
                            key: 'excessKmTrips',
                            icon: 'fa-exclamation-triangle',
                            label: 'Excess KM Alert',
                            count: excessKmTripsData.length,
                            color: excessKmTripsData.length > 0 ? '#dc2626' : '#10b981',
                            badgeBg: excessKmTripsData.length > 0 ? '#fee2e2' : '#d1fae5',
                            badgeColor: excessKmTripsData.length > 0 ? '#b91c1c' : '#047857'
                        },
                    ].map(tab => {
                        const isActive = activeTab === tab.key;
                        return (
                            <button
                                key={tab.key}
                                type="button"
                                onClick={() => setActiveTab(tab.key)}
                                className="btn d-inline-flex align-items-center gap-2"
                                style={{
                                    borderRadius: '10px',
                                    padding: '9px 18px',
                                    fontSize: '0.88rem',
                                    fontWeight: isActive ? 600 : 500,
                                    background: isActive ? '#0f172a' : '#f8fafc',
                                    color: isActive ? '#ffffff' : '#64748b',
                                    border: isActive ? '1px solid #0f172a' : '1px solid #e2e8f0',
                                    transition: 'all 0.15s ease',
                                    boxShadow: isActive ? '0 2px 6px rgba(15,23,42,0.12)' : 'none',
                                    cursor: 'pointer'
                                }}
                            >
                                <i className={`fas ${tab.icon}`} style={{ color: isActive ? '#ffffff' : tab.color, fontSize: '0.88rem' }}></i>
                                <span>{tab.label}</span>
                                <span
                                    style={{
                                        fontSize: '0.74rem',
                                        fontWeight: 700,
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        background: isActive ? 'rgba(255,255,255,0.2)' : tab.badgeBg,
                                        color: isActive ? '#ffffff' : tab.badgeColor,
                                        marginLeft: '2px'
                                    }}
                                >
                                    {tab.count}
                                </span>
                            </button>
                        );
                    })}
                </div>

                <Card.Body className="p-0">
                    {isLoading ? (
                        <div className="d-flex flex-column align-items-center justify-content-center p-5 text-muted" style={{ minHeight: '350px' }}>
                            <Spinner animation="border" variant="primary" className="mb-3" />
                            <h6 className="fw-bold" style={{ color: '#0f172a' }}>Loading Performance Data...</h6>
                            <small className="text-muted">Fetching trips and attendance records for {moment(fromDate).format('DD MMM')} - {moment(toDate).format('DD MMM YYYY')}</small>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            {/* Tab 1: Best Performers Leaderboard */}
                            {activeTab === 'bestPerformers' && (
                                <DataTable
                                    columns={bestPerformerColumns}
                                    data={bestPerformersData}
                                    pagination
                                    paginationPerPage={15}
                                    paginationRowsPerPageOptions={[10, 15, 25, 50]}
                                    highlightOnHover
                                    customStyles={customTableStyles}
                                    noDataComponent={
                                        <div className="p-5 text-center text-muted">
                                            <i className="fas fa-info-circle fa-2x mb-2 d-block"></i>
                                            No employee records found matching your filters.
                                        </div>
                                    }
                                />
                            )}

                            {/* Tab 2: Irregular Employees (Most Absent) */}
                            {activeTab === 'irregularEmployees' && (
                                <DataTable
                                    columns={irregularColumns}
                                    data={irregularEmployeesData}
                                    pagination
                                    paginationPerPage={15}
                                    paginationRowsPerPageOptions={[10, 15, 25, 50]}
                                    highlightOnHover
                                    customStyles={customTableStyles}
                                    noDataComponent={
                                        <div className="p-5 text-center text-muted">
                                            <i className="fas fa-info-circle fa-2x mb-2 d-block"></i>
                                            No irregular employee records found.
                                        </div>
                                    }
                                />
                            )}

                            {/* Tab 3: Less Visit Employees */}
                            {activeTab === 'lessVisits' && (
                                <DataTable
                                    columns={lessVisitColumns}
                                    data={lessVisitEmployeesData}
                                    pagination
                                    paginationPerPage={15}
                                    paginationRowsPerPageOptions={[10, 15, 25, 50]}
                                    highlightOnHover
                                    customStyles={customTableStyles}
                                    noDataComponent={
                                        <div className="p-5 text-center text-muted">
                                            <i className="fas fa-info-circle fa-2x mb-2 d-block"></i>
                                            No employee records found.
                                        </div>
                                    }
                                />
                            )}

                            {/* Tab 4: Next-Day Trip End Employees */}
                            {activeTab === 'nextDayTrips' && (
                                <DataTable
                                    columns={nextDayTripColumns}
                                    data={nextDayTripEmployeesData}
                                    pagination
                                    paginationPerPage={15}
                                    paginationRowsPerPageOptions={[10, 15, 25, 50]}
                                    highlightOnHover
                                    customStyles={customTableStyles}
                                    noDataComponent={
                                        <div className="p-5 text-center text-muted">
                                            <i className="fas fa-info-circle fa-2x mb-2 d-block"></i>
                                            No next-day trip end records found.
                                        </div>
                                    }
                                />
                            )}

                            {/* Tab 5: Excess KM Trips (> 1000 KM) */}
                            {activeTab === 'excessKmTrips' && (
                                <DataTable
                                    columns={excessKmColumns}
                                    data={excessKmTripsData}
                                    pagination
                                    paginationPerPage={15}
                                    paginationRowsPerPageOptions={[10, 15, 25, 50]}
                                    highlightOnHover
                                    customStyles={customTableStyles}
                                    noDataComponent={
                                        <div className="p-5 text-center text-muted">
                                            <i className="fas fa-check-circle text-success fa-2x mb-2 d-block"></i>
                                            <h6 className="fw-bold text-dark">No Excess KM Alerts!</h6>
                                            <span>No trips found with manual distance exceeding 1,000 km in this period.</span>
                                        </div>
                                    }
                                />
                            )}
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Edit Trip Modal for directly updating Start/End KM */}
            {showEditTripModal && selectedTripToEdit && (
                <EditTripModal
                    show={showEditTripModal}
                    trip={selectedTripToEdit}
                    close={() => {
                        setShowEditTripModal(false);
                        setSelectedTripToEdit(null);
                    }}
                    refetchTrip={fetchData}
                />
            )}
        </div>
    );
}
